import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { VmeCommonModule } from '../../vme-common.module';
import { RiskManagementTabsComponent } from './risk-management-tabs/risk-management-tabs.component';
import { VmePipesModule } from '../../vme-pipes.module';
import { RiskManagementFiltersComponent } from './risk-management-filters/risk-management-filters.component';
import { RiskManagementSupplierListItemComponent } from './risk-management-supplier-list-item/risk-management-supplier-list-item.component';
import { RiskManagementSelectedSupplierComponent } from './risk-management-selected-supplier/risk-management-selected-supplier.component';
import { ConfirmationService, PaginatorModule } from 'primeng';
import {
  IRiskManagementSupplier,
  ISupplierRiskManagementDTO,
} from '../../interfaces/supplier-risk-management/supplier-risk-management';
import { SupplierRiskManagementService } from '../../services/supplier-risk-management.service';
import {
  ISupplierRiskManagementSearch,
  ISupplierRiskManagementSearchFilters,
  ISupplierRiskManagementSearchFilterValues,
} from '../../interfaces/supplier-risk-management/supplier-risk-management-search';
import { ControlsOfFlat } from '../../utils/controls-of';
import { RiskManagementTab } from '../../enums/risk-management-tab.enum';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { UserService } from '../../services';
import { SupplierRiskManagementServiceType } from '../../enums/supplier-risk-management/supplier-risk-management-service-type.enum';
import { ReviewStatusChangeWithSupplier } from '../../interfaces/supplier-risk-management/review-status-change';
import { RiskManagementView } from '../../enums/supplier-risk-management/risk-management-view.enum';
import { RiskManagementViewTabsComponent } from './risk-management-view-tabs/risk-management-view-tabs.component';
import { UnscheduledTasksComponent } from './unscheduled-tasks/unscheduled-tasks.component';
import { AssignmentHistoryComponent } from './assignment-history/assignment-history.component';

@Component({
  selector: 'supplier-risk-management',
  standalone: true,
  imports: [
    CommonModule,
    VmeCommonModule,
    RiskManagementTabsComponent,
    VmePipesModule,
    RiskManagementFiltersComponent,
    RiskManagementSupplierListItemComponent,
    RiskManagementSelectedSupplierComponent,
    PaginatorModule,
    RiskManagementSelectedSupplierComponent,
    RiskManagementViewTabsComponent,
    UnscheduledTasksComponent,
    AssignmentHistoryComponent,
  ],
  templateUrl: './supplier-risk-management.component.html',
  styleUrls: ['./supplier-risk-management.component.scss'],
})
export class SupplierRiskManagementComponent implements OnInit {
  protected data: ISupplierRiskManagementDTO;
  protected paginatedRiskDetail: IRiskManagementSupplier[];
  protected selectedSupplierIndex: number = 0;

  constructor(
    private supplierRiskManagementService: SupplierRiskManagementService,
    private route: ActivatedRoute,
    private router: Router,
    private userService: UserService,
    private confirmationService: ConfirmationService,
  ) {
    this.route.queryParams.subscribe((params: Params) => {
      if (params['selectedTab'] != null) {
        this.selectedTab = Number(params['selectedTab']);
      }
      if (params['selectedView'] != null) {
        this.selectedView = Number(params['selectedView']);
      }
    });
  }

  protected selectedView: RiskManagementView = RiskManagementView.Risks;
  protected unscheduledCount: number = null;
  protected loadingUnscheduledCount: boolean = false;

  async handleViewChanged(view: RiskManagementView) {
    await this.updateQueryParam('selectedView', view.toString());
    this.selectedView = view;

    // The risks view is expensive to load, so only fetch it when it is actually on screen.
    if (view === RiskManagementView.Risks && this.data == null) {
      await this.refreshAll();
    }
  }

  /**
   * The badge is the unfiltered total, so it is not touched by the list's filters — only by a task
   * leaving the list because its schedule was set.
   */
  protected handleTaskScheduled() {
    if (this.unscheduledCount > 0) {
      this.unscheduledCount--;
    }
  }

  async loadUnscheduledCount() {
    this.loadingUnscheduledCount = true;
    try {
      this.unscheduledCount = await this.supplierRiskManagementService.getUnscheduledTasksCount();
    } finally {
      this.loadingUnscheduledCount = false;
    }
  }

  protected loadingCounts: boolean = false;
  tabCounts: Record<RiskManagementTab, number>;
  async loadCounts() {
    this.loadingCounts = true;
    let res = await this.supplierRiskManagementService.getAssigneeCounts();
    this.tabCounts = {
      [RiskManagementTab.AllSuppliers]: res.allSuppliers,
      [RiskManagementTab.AssignedToYou]: res.assignedToYou,
      [RiskManagementTab.AssignedToOthers]: res.assignedToOthers,
      [RiskManagementTab.Unassigned]: res.unassigned,
    };
    this.loadingCounts = false;
  }

  protected pageSize: number = 10;
  protected pageIndex: number = 0;

  private currentUserId: string;

  protected async refreshAll() {
    await this.loadData();
    await this.loadCounts();
  }
  async ngOnInit() {
    this.currentUserId = this.userService.currentUser.id;

    // The badge is an unfiltered total that the unscheduled list never sets, so it is always
    // fetched here regardless of which view the page opened on.
    this.loadUnscheduledCount();

    this.isUserRiskManagementAdmin = await this.userService.isUserInRole('RiskManagementAdmin');

    // The assignment history is admin only, so a non-admin arriving on it through the query param
    // is put back on the default view rather than left looking at a forbidden request.
    if (
      this.selectedView === RiskManagementView.AssignmentHistory &&
      !this.isUserRiskManagementAdmin
    ) {
      this.selectedView = RiskManagementView.Risks;
      await this.updateQueryParam('selectedView', RiskManagementView.Risks.toString());
    }

    if (this.selectedView === RiskManagementView.Risks) {
      await this.refreshAll();
    }
  }

  protected selectedTab: RiskManagementTab = RiskManagementTab.AllSuppliers;

  async handleTabChanged(tab: RiskManagementTab) {
    await this.updateQueryParam('selectedTab', tab.toString());
    this.selectedTab = tab;
    await this.loadData();
  }

  async updateQueryParam(paramName: string, paramValue: string) {
    // Get the current route's queryParams
    let queryParams = { ...this.route.snapshot.queryParams };

    // Update or set the query parameter
    queryParams[paramName] = paramValue;

    // Use navigate to update the URL without reloading the page
    await this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
      // Preserve the existing path parts of the URL.
      queryParamsHandling: 'merge',
    });
  }

  async loadData(filterValues: ControlsOfFlat<ISupplierRiskManagementSearchFilterValues> = null) {
    this.isLoading = true;
    let filters: ISupplierRiskManagementSearchFilters = {
      supplierIds: { value: filterValues?.supplierIds?.value ?? [] },
      supplierContractTypes: { value: filterValues?.supplierContractTypes?.value ?? [] },
      customerIds: { value: filterValues?.customerIds?.value ?? [] },
      riskTypes: { value: filterValues?.riskTypes?.value ?? [] },
      reviewStatuses: { value: filterValues?.reviewStatuses?.value ?? [] },
      priorities: { value: filterValues?.priorities?.value ?? [] },
      selectedTab: { value: this.selectedTab },
      serviceTypes: {
        value: filterValues?.serviceTypes?.value ?? [SupplierRiskManagementServiceType.Driven],
      },
    };

    const request: ISupplierRiskManagementSearch = {
      filters,
      /*      rows: this.pageSize,
      first: this.pageIndex,*/
      //sort field?
    };

    this.data = await this.supplierRiskManagementService.get(request);
    this.pageIndex = 0;
    this.paginateData();

    // select first
    if (this.data != null && this.data.supplierRiskDetail.length > 0) {
      this.selectSupplier(this.data.supplierRiskDetail[0].supplierId);
    }

    this.isLoading = false;
  }

  protected currentDate = Date.now();
  isLoading: boolean = true;

  protected selectSupplier(supplierId: number) {
    this.selectedSupplierIndex = this.paginatedRiskDetail.findIndex(
      (x) => x.supplierId === supplierId,
    );
  }

  protected paginate($event: any) {
    this.pageSize = $event.rows;
    this.pageIndex = $event.page;
    //event.pageCount = Total number of pages
    this.paginateData();
  }

  protected paginateData() {
    this.paginatedRiskDetail = this.data.supplierRiskDetail.slice(
      this.pageIndex * this.pageSize,
      (this.pageIndex + 1) * this.pageSize,
    );
  }

  protected trackBySupplierId(index: number, supplier: IRiskManagementSupplier): number {
    return supplier.supplierId;
  }

  protected async handleAssigneeChanged(assigneeUserId: string) {
    await this.loadCounts();
  }

  protected handleReviewStatusChange(reviewStatusChange: ReviewStatusChangeWithSupplier): void {
    let supplierIndex = this.paginatedRiskDetail.findIndex(
      (x) => x.supplierId == reviewStatusChange.supplierId,
    );
    if (supplierIndex != -1) {
      let taskIndex = this.paginatedRiskDetail[supplierIndex].tasks.findIndex(
        (x) => x.taskId == reviewStatusChange.taskId,
      );
      if (taskIndex != -1) {
        console.log('Changing review status');
        // Update the task immutably
        const updatedTasks = [...this.paginatedRiskDetail[supplierIndex].tasks];
        updatedTasks[taskIndex] = {
          ...updatedTasks[taskIndex],
          reviewStatus: reviewStatusChange.reviewStatus,
          reviewDate: new Date(), // Update review date to now
        };

        // Update the supplier immutably
        const updatedSupplier: IRiskManagementSupplier = {
          ...this.paginatedRiskDetail[supplierIndex],
          tasks: updatedTasks,
        };

        // Update the paginated array immutably
        const updatedPaginatedRiskDetail = [...this.paginatedRiskDetail];
        updatedPaginatedRiskDetail[supplierIndex] = updatedSupplier;
        this.paginatedRiskDetail = updatedPaginatedRiskDetail;

        // Also update the main data object to keep it in sync
        const mainSupplierIndex = this.data.supplierRiskDetail.findIndex(
          (x) => x.supplierId == reviewStatusChange.supplierId,
        );
        if (mainSupplierIndex != -1) {
          const updatedSupplierRiskDetail = [...this.data.supplierRiskDetail];
          updatedSupplierRiskDetail[mainSupplierIndex] = updatedSupplier;
          this.data = {
            ...this.data,
            supplierRiskDetail: updatedSupplierRiskDetail,
          };
        }
      }
    }
  }

  protected unassignMe() {
    this.confirmationService.confirm({
      message: 'Are you sure you want to unassign yourself from all suppliers?',
      header: 'Confirmation',
      accept: () => this.supplierRiskManagementService.unassignMe().then(() => this.refreshAll()),
      reject: () => {},
    });
  }

  isUserRiskManagementAdmin: boolean = false;

  protected readonly RiskManagementView = RiskManagementView;

  protected unassignAll() {
    this.confirmationService.confirm({
      message:
        'Are you sure you want to unassign all users from all suppliers? This cannot be undone.',
      header: 'Confirmation',
      accept: () => this.supplierRiskManagementService.unassignAll().then(() => this.refreshAll()),
      reject: () => {},
    });
  }
}
